import React, { useEffect, useMemo, useState } from "react";
import { LiveTrainStatus } from "../../liveTrains/types";
import "./LiveTrainsDrawer.scss";

export type LiveTrainsDrawerProps = {
  trains: LiveTrainStatus[];
  loading: boolean;
  updatedAtMs?: number;
  serviceCount: number;
  error?: string;
  selectedTrainId?: string;
  onSelectTrain: (trainId: string) => void;
  isMobile?: boolean;
};

const formatClock = (value?: number): string => {
  if (!value) {
    return "--:--";
  }
  const date = new Date(value);
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
};

const formatCountdown = (expectedEpochMs: number | undefined, nowMs: number): string => {
  if (!expectedEpochMs) {
    return "-";
  }
  const deltaMs = Math.max(0, expectedEpochMs - nowMs);
  if (deltaMs < 1000) {
    return "Due";
  }
  const totalSeconds = Math.floor(deltaMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const LiveTrainsDrawer = ({
  trains,
  loading,
  updatedAtMs,
  serviceCount,
  error,
  selectedTrainId,
  onSelectTrain,
  isMobile,
}: LiveTrainsDrawerProps): JSX.Element => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [open, setOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth > 900 : true
  );

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 900) {
        setOpen(true);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    if (selectedTrainId && !isMobile) {
      setOpen(true);
    }
  }, [selectedTrainId, isMobile]);

  const sortedTrains = useMemo(
    () =>
      [...trains].sort((a, b) => {
        if (a.phase !== b.phase) {
          return a.phase === "AtStation" ? -1 : 1;
        }
        return a.destination.localeCompare(b.destination);
      }),
    [trains]
  );

  const selectedTrain = useMemo(() => {
    if (sortedTrains.length < 1) {
      return undefined;
    }
    if (!selectedTrainId) {
      return sortedTrains[0];
    }
    return sortedTrains.find((train) => train.id === selectedTrainId) || sortedTrains[0];
  }, [sortedTrains, selectedTrainId]);

  return (
    <>
      <button
        className={`live-trains-toggle ${open ? "open" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        Live Trains ({trains.length})
      </button>
      <aside className={`live-trains-drawer ${open ? "open" : "closed"}`}>
        <header className="drawer-header">
          <div>
            <h2>Merseyrail Live</h2>
            <p>Auto-refresh every 60s</p>
          </div>
          <div className="drawer-meta">
            <span>{loading ? "Refreshing..." : "Live"}</span>
            <span>{formatClock(updatedAtMs)}</span>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={() => setOpen(false)}
            aria-label="Close live trains sidebar"
          >
            Close
          </button>
        </header>
        <div className="drawer-summary">
          <span>Active trains: {sortedTrains.length}</span>
          <span>Services scanned: {serviceCount}</span>
        </div>
        <section className="drawer-selected">
          <h3>Selected train</h3>
          {selectedTrain ? (
            <div className="selected-card">
              <div className="selected-top">
                <strong>#{selectedTrain.serviceId}</strong>
                <span>{selectedTrain.phase === "AtStation" ? "At station" : "In transit"}</span>
              </div>
              <div className="selected-main">{selectedTrain.status}</div>
              <div className="selected-grid">
                <span>Route</span>
                <span>
                  {selectedTrain.origin} → {selectedTrain.destination}
                </span>
                <span>Current</span>
                <span>{selectedTrain.currentStation || "-"}</span>
                <span>Next</span>
                <span>{selectedTrain.nextStation || "-"}</span>
                <span>Platform</span>
                <span>{selectedTrain.platform || "-"}</span>
                <span>ETA</span>
                <span>{selectedTrain.expectedTime || "-"}</span>
                <span>Arrives In</span>
                <span>{formatCountdown(selectedTrain.expectedEpochMs, nowMs)}</span>
              </div>
              <a href={selectedTrain.serviceUrl} target="_blank" rel="noreferrer">
                Open on Realtime Trains
              </a>
            </div>
          ) : (
            <div className="selected-empty">
              No active train selected. Tap a pink map dot or pick one below.
            </div>
          )}
        </section>
        {error ? <div className="drawer-error">{error}</div> : null}
        <div className="drawer-list">
          {sortedTrains.length < 1 ? (
            <div className="drawer-empty">
              {loading ? "Loading live services..." : "No active mapped trains."}
            </div>
          ) : null}
          {sortedTrains.map((train) => (
            <button
              type="button"
              className={`train-row ${selectedTrain?.id === train.id ? "selected" : ""}`}
              key={`live-train-${train.id}`}
              onClick={() => onSelectTrain(train.id)}
            >
              <div className="row-top">
                <strong>#{train.serviceId}</strong>
                <span>{train.phase === "AtStation" ? "At station" : "In transit"}</span>
              </div>
              <div className="row-main">{train.status}</div>
              <div className="row-sub">
                <span>
                  {train.origin} → {train.destination}
                </span>
              </div>
              <div className="row-sub">
                <span>Platform: {train.platform || "-"}</span>
                <span>
                  ETA: {train.expectedTime || "-"} ({formatCountdown(train.expectedEpochMs, nowMs)})
                </span>
              </div>
            </button>
          ))}
        </div>
      </aside>
    </>
  );
};
